import { Receive } from "@/types/general";
import { objectToFormData } from "@helpers/utils";
import {
    flattenPaginationRequest,
    internalApiSlice,
    PaginationRequest,
    PaginationResponse,
    transformResponse,
} from "@redux/api/internalApiSlice";

const receiveDateFields = ["date", "expiration_date", "created_at"];

function appendReceiptAttachments(formData: FormData, attachments?: File[]) {
    attachments?.forEach((file, index) => {
        formData.append(`attachments[${index}]`, file);
    });
}

export const receiptsApi = internalApiSlice.injectEndpoints({
    endpoints: (builder) => ({
        getReceiptsFull: builder.query<
            PaginationResponse<Receive.Extended>,
            PaginationRequest<Receive.DTO.Filters, Receive.DTO.OrderBy>
        >({
            query: (req) => ({
                url: "receive",
                method: "GET",
                params: {
                    ...flattenPaginationRequest(req),
                },
                parseDates: receiveDateFields,
            }),
            providesTags: (result, error, page) =>
                result
                    ? [
                          ...result.payload.map(({ id }) => ({ type: "Receive" as const, id })),
                          { type: "Receive", id: "PARTIAL-LIST" },
                      ]
                    : [{ type: "Receive", id: "PARTIAL-LIST" }],
        }),

        getModuleReceiptsFull: builder.query<
            PaginationResponse<Receive.Extended>,
            PaginationRequest<Receive.DTO.Filters, Receive.DTO.OrderBy> & { module: string }
        >({
            query: ({ module, ...req }) => ({
                url: `${module}/receive`,
                method: "GET",
                params: {
                    ...flattenPaginationRequest(req),
                },
                parseDates: receiveDateFields,
            }),
            providesTags: (result, error, page) =>
                result
                    ? [
                          ...result.payload.map(({ id }) => ({ type: "Receive" as const, id })),
                          { type: "Receive", id: "PARTIAL-LIST" },
                      ]
                    : [{ type: "Receive", id: "PARTIAL-LIST" }],
        }),

        deleteReceipt: builder.mutation<void, number>({
            query: (id) => ({
                url: `receive/${id}`,
                method: "DELETE",
            }),
            invalidatesTags: (result, error, id) => [{ type: "Receive", id }],
        }),

        createReceipt: builder.mutation<Receive.Extended, Receive.DTO.Create>({
            query: (body) => {
                const { attachments, ...payload } = body;

                if (attachments?.length) {
                    const formData = objectToFormData(payload);
                    appendReceiptAttachments(formData, attachments);

                    return {
                        url: `receive`,
                        method: "POST",
                        data: formData,
                        parseDates: receiveDateFields,
                    };
                }

                return {
                    url: `receive`,
                    method: "POST",
                    data: payload,
                    parseDates: receiveDateFields,
                };
            },
            invalidatesTags: (result, error, body) => [
                { type: "Receive", id: "PARTIAL-LIST" },
                "Stock",
                "Picking",
                "PurchaseOrder",
                "Product",
                "SaleOrder",
            ],
            transformResponse,
        }),

        getReceipt: builder.query<Receive.Extended, number>({
            query: (id) => ({
                url: `receive/${id}`,
                method: "GET",
                parseDates: receiveDateFields,
            }),
            transformResponse,
            providesTags: (result, error, id) => [{ type: "Receive", id }],
        }),

        updateReceiptDocumentNumber: builder.mutation<
            Receive.Extended,
            Receive.DTO.Update & { receiveId: number }
        >({
            query: ({ receiveId, ...body }) => ({
                url: `receive/${receiveId}`,
                method: "PATCH",
                data: body,
                parseDates: receiveDateFields,
            }),
            transformResponse,
            invalidatesTags: (result, error, { receiveId }) => [
                { type: "Receive", id: receiveId },
                { type: "Receive", id: "PARTIAL-LIST" },
            ],
        }),

        uploadReceiptAttachments: builder.mutation<
            Receive.Extended,
            { receiveId: number; attachments: File[] }
        >({
            query: ({ receiveId, attachments }) => {
                const formData = new FormData();
                appendReceiptAttachments(formData, attachments);

                return {
                    url: `receive/${receiveId}/attachment`,
                    method: "POST",
                    data: formData,
                    parseDates: receiveDateFields,
                };
            },
            transformResponse,
            invalidatesTags: (result, error, { receiveId }) => [
                { type: "Receive", id: receiveId },
                { type: "Receive", id: "PARTIAL-LIST" },
            ],
        }),

        deleteReceiptAttachment: builder.mutation<
            void,
            { receiveId: number; attachmentId: number }
        >({
            query: ({ receiveId, attachmentId }) => ({
                url: `receive/${receiveId}/attachment/${attachmentId}`,
                method: "DELETE",
            }),
            invalidatesTags: (result, error, { receiveId }) => [
                { type: "Receive", id: receiveId },
                { type: "Receive", id: "PARTIAL-LIST" },
            ],
        }),
    }),
});

export const {
    useDeleteReceiptMutation,
    useDeleteReceiptAttachmentMutation,
    useCreateReceiptMutation,
    useGetReceiptQuery,
    useGetReceiptsFullQuery,
    useGetModuleReceiptsFullQuery,
    useUpdateReceiptDocumentNumberMutation,
    useUploadReceiptAttachmentsMutation,
} = receiptsApi;
